/* Yükleme kuyruğu (plan §5.2 adım 2 ve 7).

   İki yüz kareyi tek tek işlemek dakikalar sürüyor; hepsini birden başlatmak
   ise sekmeyi çökertiyor (çözülmüş bir 100 MP kare ~390 MB yerel bellek).
   Aradaki doğru sayı dosya boyutuna bağlı ve `concurrencyFor` onu veriyor.

   Buradaki asıl karar, **kuyruğun Worker'dan ayrılması**. Sıra, eşzamanlılık,
   ilerleme, iptal ve yinelenen kare elemesi düz mantıktır ve Node'da
   sınanabilir; `OffscreenCanvas` gerektiren tek şey türev üretiminin
   kendisidir. O yüzden kuyruk bir `Runner` alır: üretimde Web Worker
   havuzu, testte sahte bir işlev.

   Bir kareyi kaybetmemek yığını durdurmaktan önemlidir: tek bir dosya
   patlarsa geri kalan 199'u yine yüklenir, hata o karenin yanında durur. */

import type { WatermarkStyle } from '@frawgraphy/shared/media';
import { planBatch, type BatchPlan, type FileLike } from './batch.ts';

export type Stage = 'bekliyor' | 'çözülüyor' | 'türetiliyor' | 'özetleniyor' | 'hazır' | 'hata';

/** Bir karenin işlenmesi için Worker'a giden iş. */
export type Job = {
  id: string;
  file: Blob;
  name: string;
  watermark: WatermarkStyle;
  year?: number | undefined;
  title?: string | undefined;
};

/** Worker'dan dönen sonuç — yükleme adımının ihtiyaç duyduğu her şey. */
export type DerivedPhoto = {
  checksum: string;
  /** EXIF çekim tarihi — `photos.captured_at` sütununa gider. */
  capturedAt?: string | null;
  /** Uygulanan EXIF yönlendirmesi; ayıklamada işe yarar. */
  orientation?: number;
  width: number;
  height: number;
  ratio: number;
  lqip: string;
  browse: { blob: Blob; width: number; height: number; bytes: number };
  full: { blob: Blob; width: number; height: number; bytes: number };
};

/** Bir işi yürüten şey. Üretimde Worker havuzu, testte sahte işlev. */
export type Runner = (job: Job, onStage: (stage: Stage) => void) => Promise<DerivedPhoto>;

export type ItemState = {
  id: string;
  name: string;
  bytes: number;
  stage: Stage;
  /** Yalnızca `hata` aşamasında dolu. */
  error?: string;
  /** Yalnızca `hazır` aşamasında dolu. */
  photo?: DerivedPhoto;
  /** Bu kare yığın içinde daha önce geçtiyse hangi karenin kopyası olduğu. */
  duplicateOf?: string;
};

export type Progress = {
  items: ItemState[];
  done: number;
  failed: number;
  duplicates: number;
  total: number;
  /** 0–1 arası; kuyruk boşaldığında 1. */
  ratio: number;
};

export type PipelineOptions = {
  runner: Runner;
  watermark?: WatermarkStyle;
  /** Verilmezse dosya boyutuna göre `concurrencyFor` karar verir. */
  concurrency?: number;
  onProgress?: (progress: Progress) => void;
  /** Kare adı → IPTC başlığı. Yönetici alt metni sonradan da girebilir. */
  titleFor?: (file: FileLike) => string | undefined;
  year?: number;
};

export type PipelineRun = {
  /** Bütün işler bittiğinde (ya da iptal edildiğinde) çözülür. */
  done: Promise<Progress>;
  /** Sırada bekleyenleri iptal eder; yürüyen işler tamamlanır. */
  cancel: () => void;
  /** Kabul edilenler, atlananlar ve öksüz ham dosyalar. */
  plan: BatchPlan<FileLike>;
};

const idFor = (index: number, name: string) => `${index}:${name}`;

/**
 * Yığını işler.
 *
 * Sonuçlar **girdi sırasında** durur; eşzamanlı bitmeleri sırayı bozmasın
 * diye her iş kendi yerine yazar. Yönetici ızgarada karelerin yer
 * değiştirmesini görmemeli.
 */
export function runPipeline<T extends FileLike & { slice?: unknown }>(
  files: readonly T[],
  options: PipelineOptions
): PipelineRun {
  const plan = planBatch(files);
  const watermark: WatermarkStyle = options.watermark ?? 'corner';
  const concurrency = Math.max(1, options.concurrency ?? plan.concurrency);

  const items: ItemState[] = plan.accepted.map((file, i) => ({
    id: idFor(i, file.name),
    name: file.name,
    bytes: file.size,
    stage: 'bekliyor'
  }));

  /* Atlanan dosyalar da listede görünür: yönetici neyin yüklenmediğini
     ızgarada görsün, uyarı metnini okumak zorunda kalmasın. */
  for (const skipped of plan.skipped) {
    items.push({
      id: `atlandı:${skipped.file.name}`,
      name: skipped.file.name,
      bytes: skipped.file.size,
      stage: 'hata',
      error: skipped.message
    });
  }

  const seen = new Map<string, string>();
  let cancelled = false;
  let next = 0;

  const snapshot = (): Progress => {
    const done = items.filter((i) => i.stage === 'hazır').length;
    const failed = items.filter((i) => i.stage === 'hata').length;
    const duplicates = items.filter((i) => i.duplicateOf !== undefined).length;
    return {
      items: items.map((i) => ({ ...i })),
      done,
      failed,
      duplicates,
      total: items.length,
      ratio: items.length === 0 ? 1 : (done + failed) / items.length
    };
  };

  const report = () => options.onProgress?.(snapshot());

  async function worker(): Promise<void> {
    for (;;) {
      if (cancelled) return;
      const index = next++;
      const file = plan.accepted[index];
      if (!file) return;

      const item = items[index]!;
      const job: Job = {
        id: item.id,
        file: file as unknown as Blob,
        name: file.name,
        watermark,
        year: options.year,
        title: options.titleFor?.(file)
      };

      try {
        const photo = await options.runner(job, (stage) => {
          item.stage = stage;
          report();
        });

        /* Aynı çekimi ikinci kez bırakmak yeni satır doğurmamalı (plan §5.2
           adım 5). Yığın içindeki tekrar burada yakalanır; veritabanındaki
           tekrarı `photos_checksum_idx` yakalar. */
        const earlier = seen.get(photo.checksum);
        if (earlier) {
          item.duplicateOf = earlier;
        } else {
          seen.set(photo.checksum, item.id);
        }

        item.photo = photo;
        item.stage = 'hazır';
      } catch (err) {
        /* Tek dosyanın patlaması yığını durdurmaz. */
        item.stage = 'hata';
        item.error = err instanceof Error ? err.message : String(err);
      }
      report();
    }
  }

  const done = (async () => {
    report();
    await Promise.all(Array.from({ length: concurrency }, () => worker()));
    return snapshot();
  })();

  return {
    done,
    cancel: () => {
      cancelled = true;
      for (const item of items) {
        if (item.stage === 'bekliyor') {
          item.stage = 'hata';
          item.error = 'iptal edildi';
        }
      }
      report();
    },
    plan
  };
}

/**
 * Web Worker havuzundan bir `Runner` kurar.
 *
 * `createWorker` dışarıdan gelir: Worker adresini kurmak paketleyiciye özgü
 * bir iştir (Vite'ta `new URL('./worker.ts', import.meta.url)`), ve o bilgi
 * bu pakete sızmamalı.
 */
export function workerRunner(createWorker: () => Worker, poolSize: number): Runner & {
  dispose: () => void;
} {
  const pool: Worker[] = [];
  const idle: Worker[] = [];
  const waiting: ((worker: Worker) => void)[] = [];

  const acquire = (): Promise<Worker> => {
    const free = idle.pop();
    if (free) return Promise.resolve(free);
    if (pool.length < poolSize) {
      const created = createWorker();
      pool.push(created);
      return Promise.resolve(created);
    }
    return new Promise((resolve) => waiting.push(resolve));
  };

  const release = (worker: Worker) => {
    const next = waiting.shift();
    if (next) next(worker);
    else idle.push(worker);
  };

  const runner: Runner = async (job, onStage) => {
    const worker = await acquire();
    try {
      return await new Promise<DerivedPhoto>((resolve, reject) => {
        const onMessage = (event: MessageEvent) => {
          const data = event.data as WorkerOut;
          if (data.id !== job.id) return;
          if (data.type === 'stage') {
            onStage(data.stage);
            return;
          }
          worker.removeEventListener('message', onMessage);
          worker.removeEventListener('error', onError);
          if (data.type === 'done') resolve(data.photo);
          else reject(new Error(data.message));
        };
        const onError = (event: ErrorEvent) => {
          worker.removeEventListener('message', onMessage);
          worker.removeEventListener('error', onError);
          reject(new Error(event.message || 'Worker çöktü'));
        };
        worker.addEventListener('message', onMessage);
        worker.addEventListener('error', onError);
        worker.postMessage({ type: 'derive', job } satisfies WorkerIn);
      });
    } finally {
      release(worker);
    }
  };

  return Object.assign(runner, {
    dispose: () => {
      for (const worker of pool) worker.terminate();
      pool.length = 0;
      idle.length = 0;
    }
  });
}

/* ── Worker mesaj sözleşmesi ────────────────────────────── */

export type WorkerIn = { type: 'derive'; job: Job };

export type WorkerOut =
  | { type: 'stage'; id: string; stage: Stage }
  | { type: 'done'; id: string; photo: DerivedPhoto }
  | { type: 'failed'; id: string; message: string };
